import { format } from 'date-fns';
import i18n from '../../i18n';
import { formatCandidateDisplayValue } from './formatCandidateField';

const ALERT_TYPE_CLASSNAMES = {
  open_to_work: 'bg-green-100 text-green-800 border-green-200',
  potential_open_to_work: 'bg-green-50 text-green-700 border-green-200',
  new_post: 'bg-blue-100 text-blue-800 border-blue-200',
  new_certification: 'bg-violet-100 text-violet-800 border-violet-200',
  new_skills: 'bg-violet-100 text-violet-800 border-violet-200',
  update: 'bg-gray-100 text-gray-800 border-gray-200',
};

const ALERT_TYPE_LABEL_KEYS = {
  open_to_work: 'alerts.typeOpenToWork',
  potential_open_to_work: 'alerts.typePotentialOpenToWork',
  new_post: 'alerts.typeNewPost',
  new_certification: 'alerts.typeNewCertification',
  new_skills: 'alerts.typeNewSkills',
  update: 'alerts.typeUpdate',
};

const FALLBACK_DESCRIPTION_KEYS = {
  open_to_work: 'alerts.fallbackOpenToWork',
  potential_open_to_work: 'alerts.fallbackPotential',
  new_post: 'alerts.fallbackNewPost',
  new_certification: 'alerts.fallbackCertification',
  new_skills: 'alerts.fallbackSkills',
};

export const getAlertTypeConfig = (alertType) => {
  const labelKey = ALERT_TYPE_LABEL_KEYS[alertType] || 'alerts.typeUpdate';
  return {
    label: i18n.t(labelKey),
    className: ALERT_TYPE_CLASSNAMES[alertType] || ALERT_TYPE_CLASSNAMES.update,
  };
};

export const getAlertDescription = (alert) => {
  const description = alert?.description?.trim?.();
  if (description) return description;

  const name = alert?.candidate?.full_name || i18n.t('common.candidate');
  const key = FALLBACK_DESCRIPTION_KEYS[alert?.alert_type];
  return key ? i18n.t(key, { name }) : i18n.t('alerts.fallbackUpdate', { name });
};

export const transformAlert = (alert) => {
  const typeConfig = getAlertTypeConfig(alert.alert_type);
  const candidateName = alert.candidate?.full_name || i18n.t('common.unknown');

  return {
    id: alert.id,
    candidateId: alert.candidate_id,
    candidateName,
    avatar: alert.candidate?.avatar || null,
    avatarAlt: `${candidateName} profile`,
    currentRole: formatCandidateDisplayValue(
      alert.candidate?.current_position || i18n.t('common.notSpecified'),
    ),
    isFromZoho: Boolean(String(alert.candidate?.zoho_id ?? '').trim()),
    description: getAlertDescription(alert),
    alertType: alert.alert_type,
    alertTypeLabel: typeConfig.label,
    alertTypeClassName: typeConfig.className,
    time: alert.created_at ? format(new Date(alert.created_at), 'dd-MM-yyyy HH:mm') : i18n.t('common.emDash'),
    status: alert.status,
    createdAt: alert.created_at,
  };
};

/** @deprecated Use transformAlert */
export const transformTodayAlert = transformAlert;

export const getAlertTypeFilterOptions = () => [
  { value: 'all', label: i18n.t('alerts.typeAll') },
  { value: 'open_to_work', label: i18n.t('alerts.typeOpenToWork') },
  { value: 'potential_open_to_work', label: i18n.t('alerts.typePotentialOpenToWork') },
  { value: 'new_certification', label: i18n.t('alerts.typeNewCertification') },
  { value: 'new_skills', label: i18n.t('alerts.typeNewSkills') },
  { value: 'update', label: i18n.t('alerts.typeUpdate') },
];

/** @deprecated Prefer getAlertTypeFilterOptions() so labels follow the active locale */
export const ALERT_TYPE_FILTER_OPTIONS = getAlertTypeFilterOptions();

export const getAlertTypeFilterLabels = () => Object.fromEntries(
  getAlertTypeFilterOptions()
    .filter((option) => option.value !== 'all')
    .map((option) => [option.value, option.label]),
);

/** @deprecated Prefer getAlertTypeFilterLabels() */
export const ALERT_TYPE_FILTER_LABELS = getAlertTypeFilterLabels();

export const matchesAlertTypeFilter = (alertType, filter) => {
  if (filter === 'all') return true;
  if (filter === 'update') {
    return !ALERT_TYPE_LABEL_KEYS[alertType] || alertType === 'update';
  }
  return alertType === filter;
};
